
/**
 * LTX-2 Frontend Service
 * 
 * Handles interaction with the LTX-2 video generation API via the backend.
 * Supports BYOK (Bring Your Own Key) for Hugging Face / LTX API.
 * 
 * Full feature set including:
 * - Text/Image/Video-to-Video generation
 * - Synchronized audio generation
 * - Camera motion control
 * - IC-LoRA control models (Canny/Depth/Pose)
 * - Multi-keyframe animation
 * - Prompt enhancement
 */

import { LTXParams, LTXCameraMotion, LTXKeyframe } from '../types';

export interface LTXGenerateRequest {
    prompt: string;
    mode: 't2v' | 'i2v' | 'v2v' | 'keyframe' | 'a2v';

    // Input media
    image?: string;               // Base64 for i2v
    video?: string;               // Base64 or URL for v2v
    keyframes?: LTXKeyframe[];    // For keyframe mode

    // Model selection
    model?: 'ltx-2-fast' | 'ltx-2-pro' | 'ltx-2-ultra';

    // Core parameters
    parameters?: {
        guidance_scale?: number;
        num_inference_steps?: number;
        seed?: number;
        negative_prompt?: string;

        // Output settings
        duration_sec?: number;
        resolution?: '720p' | '1080p' | '1440p' | '4k';
        fps?: 24 | 25 | 30 | 50;
        aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:3';

        // Audio
        generate_audio?: boolean;
        audio_prompt?: string;

        // Camera
        camera_motion?: LTXCameraMotion;

        // Control models
        control_type?: 'none' | 'canny' | 'depth' | 'pose';
        control_image?: string;
        control_strength?: number;

        // Enhancement
        enhance_prompt?: boolean;

        // Retake
        retake_source_video?: string;
        retake_preserve?: ('structure' | 'motion' | 'colors' | 'audio')[];
    };

    apiKey?: string; // Hugging Face / LTX API Key
}

export interface LTXGenerateResponse {
    success: boolean;
    videoUrl: string;
    audioUrl?: string;            // Separate audio track if needed
    error?: string;
    estimated_time?: number;      // If model is loading
    metadata?: {
        duration_sec: number;
        resolution: string;
        fps: number;
        has_audio: boolean;
    };
}

export interface LTXModelInfo {
    id: string;
    name: string;
    status: 'ready' | 'loading' | 'unavailable';
    estimated_time?: number;
}

const API_BASE = '/api/ltx';

/**
 * Get API key for LTX (BYOK mode)
 * Checks localStorage for 'huggingface_api_key' or 'ltx_api_key'
 */
function getStoredApiKey(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('huggingface_api_key') || localStorage.getItem('ltx_api_key');
}

/**
 * Convert LTXParams to API request format
 */
export function paramsToRequest(
    prompt: string,
    params: LTXParams,
    inputImage?: string,
    inputVideo?: string
): LTXGenerateRequest {
    return {
        prompt,
        mode: params.mode,
        model: params.model,
        image: params.mode === 'i2v' ? inputImage : undefined,
        video: params.mode === 'v2v' ? inputVideo : undefined,
        keyframes: params.mode === 'keyframe' ? params.keyframes : undefined,
        parameters: {
            guidance_scale: params.guidance_scale,
            num_inference_steps: params.num_inference_steps,
            seed: params.seed,
            negative_prompt: params.negative_prompt,
            duration_sec: params.duration_sec,
            resolution: params.resolution,
            fps: params.fps,
            aspect_ratio: params.aspect_ratio,
            generate_audio: params.generate_audio,
            audio_prompt: params.audio_prompt,
            camera_motion: params.camera_motion,
            control_type: params.control_type,
            control_image: params.control_image,
            control_strength: params.control_strength,
            enhance_prompt: params.enhance_prompt,
            retake_source_video: params.retake_source_video,
            retake_preserve: params.retake_preserve,
        },
    };
}

/**
 * Main video generation function
 */
export async function generateVideo(request: LTXGenerateRequest): Promise<LTXGenerateResponse> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    // BYOK: Pass API key if available
    const apiKey = request.apiKey || getStoredApiKey();
    if (apiKey) {
        headers['x-huggingface-token'] = apiKey;
        headers['x-ltx-api-key'] = apiKey;
    }

    try {
        console.log('[LTXService] Starting generation:', {
            mode: request.mode,
            model: request.model,
            duration: request.parameters?.duration_sec,
            resolution: request.parameters?.resolution,
            audio: request.parameters?.generate_audio,
            camera: request.parameters?.camera_motion,
        });

        const response = await fetch(`${API_BASE}/generate`, {
            method: 'POST',
            headers,
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

            // Handle Model Loading State
            if (response.status === 503 && errorData.error === 'MODEL_LOADING') {
                return {
                    success: false,
                    videoUrl: '',
                    error: 'MODEL_LOADING',
                    estimated_time: errorData.estimated_time
                };
            }

            // Handle Auth Error
            if (response.status === 401) {
                throw new Error('API_KEY_INVALID');
            }

            // Handle Rate Limit
            if (response.status === 429) {
                throw new Error('RATE_LIMIT_EXCEEDED');
            }

            throw new Error(errorData.error || `API Error: ${response.status}`);
        }

        const data = await response.json();
        console.log('[LTXService] Generation complete:', data.metadata);
        return data;

    } catch (error: any) {
        console.error('[LTXService] Generate error:', error);
        throw error;
    }
}

/**
 * Check model status (useful for preloading)
 */
export async function checkModelStatus(modelId: string): Promise<LTXModelInfo> {
    try {
        const response = await fetch(`${API_BASE}/status/${modelId}`);
        if (!response.ok) {
            return { id: modelId, name: modelId, status: 'unavailable' };
        }
        return await response.json();
    } catch {
        return { id: modelId, name: modelId, status: 'unavailable' };
    }
}

/**
 * Cancel an ongoing generation (if supported)
 */
export async function cancelGeneration(requestId: string): Promise<boolean> {
    try {
        const response = await fetch(`${API_BASE}/cancel/${requestId}`, { method: 'POST' });
        return response.ok;
    } catch {
        return false;
    }
}
